import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import ReactPaginate from "react-paginate";
import { useApp } from "../../Context/AppContext";
import HeroSection from "../HeroSection";


// ─── Sort ────────────────────────────────────────────────────────────────────
const SORT_OPTIONS = [
  { value: "a-to-z", label: "Name: A → Z" },
  { value: "z-to-a", label: "Name: Z → A" },
  { value: "popular", label: "Most Popular" },
];

const SortDropdown = ({ sortOrder, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = SORT_OPTIONS.find((o) => o.value === sortOrder) || SORT_OPTIONS[0];

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-2 bg-white border border-[#e2eaea] hover:border-[#5fbdb3] text-[#374747] text-[13px] font-semibold px-4 h-10 rounded-xl transition-all duration-200 font-poppins whitespace-nowrap"
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="4" y1="6" x2="20" y2="6" />
          <line x1="8" y1="12" x2="16" y2="12" />
          <line x1="11" y1="18" x2="13" y2="18" />
        </svg>
        Sort: {current.label}
        <svg className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-50 bg-white border border-[#e2eaea] rounded-2xl shadow-[0_8px_32px_rgba(22,44,42,0.16)] overflow-hidden min-w-[200px]">
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`w-full flex items-center justify-between px-4 py-3 text-[13px] font-semibold text-left transition-all font-poppins ${sortOrder === opt.value ? "bg-[#eaf4f3] text-[#1e4a46]" : "text-[#374747] hover:bg-[#f8fafa] hover:text-[#1e4a46]"}`}
            >
              {opt.label}
              {sortOrder === opt.value && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#1e4a46" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Hero icon ───────────────────────────────────────────────────────────────
const BrandIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#5fbdb3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);

// ─── Fallback image ───────────────────────────────────────────────────────────
const FallbackIcon = () => (
  <svg width="100%" height="100%" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="60" cy="60" r="52" fill="#f4fbfa" stroke="#d4ecea" strokeWidth="1.5" />
    <rect x="18" y="47" width="84" height="26" rx="13" fill="#eaf4f3" stroke="#c5d5d4" strokeWidth="1.5" />
    <path d="M18 60C18 52.8 23.8 47 31 47H60V73H31C23.8 73 18 67.2 18 60Z" fill="#5fbdb3" fillOpacity="0.55" />
    <path d="M102 60C102 67.2 96.2 73 89 73H60V47H89C96.2 47 102 52.8 102 60Z" fill="#ff601e" fillOpacity="0.50" />
    <line x1="60" y1="47" x2="60" y2="73" stroke="white" strokeWidth="1.5" strokeDasharray="3 2.5" />
  </svg>
);

// ─── Theme palette (matches Concern alternating pattern) ─────────────────────
const BRAND_THEMES = [
  {
    cardBg: "bg-gradient-to-b from-[#eaf4f3] to-white",
    cardBorder: "border-[#e2eaea] hover:border-[#5fbdb3]",
    accentBar: "from-[#1e4a46] via-[#5fbdb3] to-[#1e4a46]",
    imgBg: "bg-[#f4fbfa]",
    shadow: "shadow-[0_2px_12px_rgba(95,189,179,0.09)] hover:shadow-[0_10px_28px_rgba(95,189,179,0.22)]",
    btnCls: "bg-[#1e4a46] hover:bg-[#ff601e] shadow-[0_2px_8px_rgba(22,44,42,0.2)] hover:shadow-[0_4px_14px_rgba(255,96,30,0.4)]",
    countColor: "text-[#5fbdb3]",
  },
  {
    cardBg: "bg-gradient-to-b from-[#fff3ee] to-white",
    cardBorder: "border-[#ffd5c2] hover:border-[#ff601e]/60",
    accentBar: "from-[#ff601e] to-[#e55519]",
    imgBg: "bg-[#fff8f5]",
    shadow: "shadow-[0_2px_12px_rgba(255,96,30,0.09)] hover:shadow-[0_10px_28px_rgba(255,96,30,0.22)]",
    btnCls: "bg-[#ff601e] hover:bg-[#e55519] shadow-[0_2px_8px_rgba(255,96,30,0.3)]",
    countColor: "text-[#ff601e]",
  },
];

// ─── Alpha sidebar letters ────────────────────────────────────────────────────
const ALPHABET = "#ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

// ─── Brand Card (own component so hooks are valid) ────────────────────────────
const BrandCard = ({ brand, index, onClick }) => {
  const [imgError, setImgError] = useState(false);
  const hasImage = brand.image_url && !imgError;
  const t = BRAND_THEMES[index % BRAND_THEMES.length];

  return (
    <div
      className={`group border-[1.5px] ${t.cardBorder} ${t.cardBg} ${t.shadow} rounded-2xl overflow-hidden flex flex-col relative hover:-translate-y-2 transition-all duration-300 cursor-pointer`}
      style={{ animation: `fadeInUp 0.35s ease ${(index % 8) * 40}ms both` }}
      onClick={onClick}
    >
      {/* Top accent bar */}
      <div className={`absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r ${t.accentBar} opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10`} />

      {/* Image area */}
      <div className={`w-full h-[150px] sm:h-[170px] lg:h-[190px] ${t.imgBg} flex items-center justify-center overflow-hidden p-6`}>
        {hasImage ? (
          <img
            src={brand.image_url}
            alt={brand.name}
            onError={() => setImgError(true)}
            className="max-w-full max-h-full object-contain group-hover:scale-110 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full p-2"><FallbackIcon /></div>
        )}
      </div>

      <div className="w-full h-px bg-[#f1f5f5]" />

      {/* Info */}
      <div className="px-3 sm:px-4 pt-3 pb-2 flex-1 flex flex-col gap-1.5">
        <h3 className="font-semibold text-[12px] sm:text-[13px] text-[#1e4a46] line-clamp-2 leading-snug group-hover:text-[#ff601e] transition-colors duration-200">
          {brand.name || "Unknown Brand"}
        </h3>
      </div>

      {/* CTA */}
      <div className="px-3 sm:px-4 pb-4 pt-1">
        <button
          className={`w-full h-10 ${t.btnCls} text-white text-[11px] sm:text-[12px] font-semibold rounded-xl flex items-center justify-center gap-1.5 transition-all duration-200`}
          onClick={(e) => { e.stopPropagation(); onClick(); }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
            <line x1="7" y1="7" x2="7.01" y2="7" />
          </svg>
          View Products
        </button>
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const ShopByBrand = () => {
  const { contextBrands } = useApp();
  const navigate = useNavigate();

  const [brands, setBrands] = useState([]);
  const [filteredBrands, setFilteredBrands] = useState([]);
  const [heroSearch, setHeroSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("a-to-z");
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const brandsPerPage = 20;

  const dropdownRef = useRef(null);

  // Build brand list from context
  useEffect(() => {
    const formatted = contextBrands.map((b) => ({
      id: b.manufacturer_id,
      name: b.manufacturer_name || b.name || "",
      image_url: b.image_url || null,
    }));
    setBrands(formatted);
    setFilteredBrands(formatted);
  }, [contextBrands]);

  // Close sidebar on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setSidebarOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Apply filters: search + letter + sort
  useEffect(() => {
    let result = [...brands];

    if (heroSearch.trim()) {
      result = result.filter((b) =>
        b.name.toLowerCase().includes(heroSearch.toLowerCase())
      );
    }

    if (selectedLetter) {
      if (selectedLetter === "#") {
        result = result.filter((b) => /^[^a-zA-Z]/.test(b.name));
      } else {
        result = result.filter((b) =>
          b.name.toUpperCase().startsWith(selectedLetter)
        );
      }
    }

    if (sortOrder === "a-to-z") result.sort((a, b) => a.name.localeCompare(b.name));
    else if (sortOrder === "z-to-a") result.sort((a, b) => b.name.localeCompare(a.name));

    setFilteredBrands(result);
    setCurrentPage(0);
  }, [heroSearch, selectedLetter, sortOrder, brands]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleBrandClick = (brand) => {
    if (!brand.name) return;
    const slug = brand.name.toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-");
    navigate(`/shop-by-brand/${slug}`, {
      state: { manufacturer_id: brand.id, manufacturer_name: brand.name },
    });
  };

  const offset = currentPage * brandsPerPage;
  const currentBrands = filteredBrands.slice(offset, offset + brandsPerPage);
  const pageCount = Math.ceil(filteredBrands.length / brandsPerPage);

  return (
    <div className="min-h-screen bg-[#f8fafa] font-poppins">
      <Helmet>
        <title>Shop by Brand | MediSYS Pharmacy</title>
        <meta name="description" content="Browse medicines from your favourite pharmaceutical brands at MediSYS Pharmacy." />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* ── Hero ── */}
      <HeroSection
        breadcrumbs={[
          { label: "Home", to: "/home" },
          { label: "Shop by Brand" },
        ]}
        title="Shop by Brand"
        subtitle={
          filteredBrands.length > 0
            ? `${filteredBrands.length} brand${filteredBrands.length !== 1 ? "s" : ""} available`
            : "Explore all brands"
        }
        badge="Trusted Names"
        icon={<BrandIcon />}
        accentColor="#5fbdb3"
      />

      {/* ── Search + Sort bar ── */}
      <div className="bg-white border-b border-[#e2eaea] px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          {/* Search input */}
          <div className="flex items-center gap-2.5 flex-1 bg-[#f8fafa] border border-[#e2eaea] focus-within:border-[#5fbdb3] focus-within:bg-white rounded-xl px-4 h-10 transition-all duration-200">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8faba9" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              value={heroSearch}
              onChange={(e) => setHeroSearch(e.target.value)}
              placeholder="Search brands…"
              className="flex-1 bg-transparent outline-none text-[13px] font-medium text-[#1e4a46] placeholder-[#8faba9] font-poppins"
            />
            {heroSearch && (
              <button onClick={() => setHeroSearch("")} className="text-[#8faba9] hover:text-[#1e4a46] transition-colors">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Mobile filter toggle */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden flex items-center gap-2 text-[13px] font-semibold text-[#1e4a46] border border-[#d4ecea] bg-[#eaf4f3] px-4 h-10 rounded-xl"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="4" y1="6" x2="20" y2="6" />
                <line x1="8" y1="12" x2="20" y2="12" />
                <line x1="12" y1="18" x2="20" y2="18" />
              </svg>
              A–Z
              {selectedLetter && (
                <span className="bg-[#ff601e] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {selectedLetter}
                </span>
              )}
            </button>

            <SortDropdown sortOrder={sortOrder} onChange={(v) => { setSortOrder(v); setCurrentPage(0); }} />

            <div className="hidden sm:flex items-center gap-1.5 bg-[#eaf4f3] border border-[#d4ecea] rounded-xl px-3 h-10 flex-shrink-0">
              <span className="font-bold text-[13px] text-[#1e4a46]">{filteredBrands.length}</span>
              <span className="text-[12px] text-[#5fbdb3] font-medium">brands</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row">
        {/* ── Sidebar: A–Z filter ── */}
        <aside
          ref={dropdownRef}
          className={`${sidebarOpen ? "block" : "hidden"} lg:block lg:w-[260px] xl:w-[280px] flex-shrink-0 bg-white border-r border-[#e2eaea]`}
        >
          <div className="sticky top-0 p-5 space-y-4 max-h-[calc(100vh-120px)] overflow-y-auto">
            <div className="px-3">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-[14px] text-[#ff601e]">Browse A–Z</h3>
                {selectedLetter && (
                  <button
                    onClick={() => setSelectedLetter(null)}
                    className="text-[11px] font-semibold text-[#ff601e] hover:underline"
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {ALPHABET.map((letter) => {
                  const isActive = selectedLetter === letter;
                  return (
                    <button
                      key={letter}
                      onClick={() => {
                        setSelectedLetter(isActive ? null : letter);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                      className={`w-8 h-8 rounded-lg text-[12px] font-bold flex items-center justify-center transition-all duration-150 border-[1.5px]
                        ${isActive
                          ? "bg-[#1e4a46] text-white border-[#1e4a46] shadow-[0_2px_8px_rgba(22,44,42,0.25)]"
                          : "bg-white text-[#374747] border-[#e2eaea] hover:bg-[#eaf4f3] hover:border-[#5fbdb3] hover:text-[#1e4a46]"
                        }`}
                    >
                      {letter}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </aside>

        {/* ── Brand Grid ── */}
        <div className="flex-1 px-4 sm:px-6 lg:px-8 py-6">
          {currentBrands.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-16 h-16 rounded-2xl bg-[#eaf4f3] border border-[#d4ecea] flex items-center justify-center">
                <BrandIcon />
              </div>
              <p className="font-semibold text-[15px] text-[#374747]">No brands found</p>
              <p className="text-[13px] text-[#8faba9]">Try a different letter or search term.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              {currentBrands.map((brand, index) => (
                <BrandCard
                  key={brand.id}
                  brand={brand}
                  index={index}
                  onClick={() => handleBrandClick(brand)}
                />
              ))}
            </div>
          )}

          {/* ── Pagination ── */}
          {pageCount > 1 && (
            <div className="flex justify-center mt-8">
              <ReactPaginate
                previousLabel={
                  <span className="flex items-center gap-1">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M19 12H5M12 5l-7 7 7 7" />
                    </svg>
                    Prev
                  </span>
                }
                nextLabel={
                  <span className="flex items-center gap-1">
                    Next
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </span>
                }
                breakLabel="…"
                pageCount={pageCount}
                marginPagesDisplayed={1}
                pageRangeDisplayed={3}
                onPageChange={({ selected }) => {
                  setCurrentPage(selected);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                containerClassName="flex items-center gap-2 flex-wrap justify-center"
                pageClassName="w-9 h-9 rounded-xl border-[1.5px] border-[#e2eaea] bg-white text-[#374747] flex items-center justify-center text-[13px] font-semibold hover:bg-[#eaf4f3] hover:border-[#5fbdb3] cursor-pointer transition-all"
                activeClassName="!bg-[#1e4a46] !border-[#1e4a46] !text-white shadow-[0_2px_8px_rgba(22,44,42,0.25)]"
                previousClassName="flex items-center gap-1 h-9 px-3 rounded-xl border-[1.5px] border-[#e2eaea] bg-white text-[#374747] text-[12px] font-semibold hover:bg-[#eaf4f3] hover:border-[#5fbdb3] cursor-pointer transition-all"
                nextClassName="flex items-center gap-1 h-9 px-3 rounded-xl border-[1.5px] border-[#e2eaea] bg-white text-[#374747] text-[12px] font-semibold hover:bg-[#eaf4f3] hover:border-[#5fbdb3] cursor-pointer transition-all"
                disabledClassName="opacity-40 cursor-not-allowed hover:bg-white hover:border-[#e2eaea]"
                breakClassName="text-[#8faba9] text-[13px] px-1 flex items-center"
              />
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
      `}</style>
    </div>
  );
};

export default ShopByBrand;